import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { FiUsers, FiBriefcase, FiAlertCircle, FiCheckCircle, FiClock, FiArrowRight, FiSearch, FiRefreshCw } from 'react-icons/fi'
import toast from 'react-hot-toast'
import { asesoriaAPI } from '../services/api'
import { PageHeader, Btn } from '../ui'
import { formatearFecha } from '../utils/fechas'

const EstadoBadge = ({ status, fechaExpiracion }) => {
  const expirada = fechaExpiracion && new Date(fechaExpiracion) <= new Date()
  if (expirada || status === 'expired' || status === 'canceled') {
    return <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-red-100 text-red-700">Expirada</span>
  }
  if (status === 'trialing') {
    return <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-blue-100 text-blue-700">Trial</span>
  }
  if (status === 'active') {
    return <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-green-100 text-green-700">Activa</span>
  }
  return <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-[var(--bg-2)] text-[var(--ink-2)]">{status || '—'}</span>
}

const PlanBadge = ({ plan }) => {
  const colors = {
    avanzado: 'bg-purple-100 text-purple-700',
    pro: 'bg-blue-100 text-blue-700',
  }
  return (
    <span className={`px-2 py-0.5 text-xs font-semibold rounded-full capitalize ${colors[plan] || 'bg-[var(--bg-2)] text-[var(--ink-2)]'}`}>
      {plan || '—'}
    </span>
  )
}

const MisClientes = () => {
  const navigate = useNavigate()
  const [clientes, setClientes] = useState([])
  const [loading, setLoading] = useState(true)
  const [busqueda, setBusqueda] = useState('')

  useEffect(() => { cargarClientes() }, [])

  const cargarClientes = async () => {
    setLoading(true)
    try {
      const res = await asesoriaAPI.getMisClientes()
      setClientes(res.data || [])
    } catch {
      toast.error('Error al cargar clientes')
    } finally {
      setLoading(false)
    }
  }

  const clientesFiltrados = clientes.filter(c =>
    c.nombre?.toLowerCase().includes(busqueda.toLowerCase())
  )

  const totalAlertasDias = clientes.reduce((s, c) => s + (c.dias_sin_fichaje || 0), 0)

  return (
    <div className="space-y-6">
      <PageHeader
        num="§ AC"
        kicker="Mis clientes"
        title="Empresas"
        italic="vinculadas."
        sub={
          totalAlertasDias > 0
            ? `${clientes.length} ${clientes.length === 1 ? 'empresa vinculada' : 'empresas vinculadas'} · ${totalAlertasDias} día${totalAlertasDias !== 1 ? 's' : ''} sin fichaje pendientes.`
            : `${clientes.length} ${clientes.length === 1 ? 'empresa vinculada' : 'empresas vinculadas'}.`
        }
        actions={
          <Btn variant="ghost" size="sm" onClick={cargarClientes} disabled={loading}>
            <FiRefreshCw className={loading ? 'animate-spin' : ''} size={14} /> Refrescar
          </Btn>
        }
        className="!px-0 !pt-0 !pb-5 !border-0"
      />

      {/* Buscador */}
      <div className="relative mb-6">
        <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[var(--ink-4)]" />
        <input
          type="text"
          placeholder="Buscar empresa..."
          value={busqueda}
          onChange={e => setBusqueda(e.target.value)}
          className="px-3 py-2 bg-[var(--bg-2)] border border-[var(--line)] rounded-soft text-[14px] pl-10 w-full max-w-sm"
        />
      </div>

      {/* Lista de clientes */}
      {loading ? (
        <div className="space-y-3">
          {[1, 2, 3].map(i => (
            <div key={i} className="ui-card p-5 animate-pulse h-24 bg-[var(--bg-2)]" />
          ))}
        </div>
      ) : clientesFiltrados.length === 0 ? (
        <div className="ui-card p-5 text-center py-16">
          <FiBriefcase className="mx-auto h-12 w-12 text-gray-300 mb-3" />
          <p className="text-[var(--ink-3)] font-medium">
            {busqueda ? 'No hay clientes que coincidan' : 'No hay clientes vinculados aún'}
          </p>
          {!busqueda && (
            <p className="text-sm text-[var(--ink-4)] mt-1">
              Contacta con nosotros para vincular las empresas de tus clientes
            </p>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          {clientesFiltrados.map(cliente => (
            <div
              key={cliente.id}
              className="ui-card p-5 hover:shadow-md transition-shadow cursor-pointer group"
              onClick={() => navigate(`/asesoria/empresa/${cliente.id}`)}
            >
              <div className="flex items-center gap-4">
                {/* Avatar */}
                <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-teal-400 to-teal-600 flex items-center justify-center flex-shrink-0 text-white font-bold text-lg">
                  {cliente.nombre?.charAt(0).toUpperCase()}
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <span className="font-semibold text-[var(--ink)] truncate">{cliente.nombre}</span>
                    <PlanBadge plan={cliente.plan} />
                    <EstadoBadge status={cliente.subscription_status} fechaExpiracion={cliente.fecha_expiracion} />
                    {cliente.nivel_acceso === 'readonly' && (
                      <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-yellow-100 text-yellow-700">Solo lectura</span>
                    )}
                  </div>
                  <div className="flex items-center gap-4 text-xs text-[var(--ink-3)]">
                    <span className="flex items-center gap-1">
                      <FiUsers className="h-3.5 w-3.5" />
                      {cliente.empleados_activos} empleado{cliente.empleados_activos !== 1 ? 's' : ''}
                    </span>
                    {cliente.dias_sin_fichaje > 0 && (
                      <span className="flex items-center gap-1 text-amber-600 font-medium">
                        <FiAlertCircle className="h-3.5 w-3.5" />
                        {cliente.dias_sin_fichaje} día{cliente.dias_sin_fichaje !== 1 ? 's' : ''} sin fichaje
                      </span>
                    )}
                    {cliente.dias_sin_fichaje === 0 && (
                      <span className="flex items-center gap-1 text-green-600">
                        <FiCheckCircle className="h-3.5 w-3.5" />
                        Sin incidencias
                      </span>
                    )}
                    <span className="flex items-center gap-1 text-[var(--ink-4)]">
                      <FiClock className="h-3.5 w-3.5" />
                      Vinculado {formatearFecha(cliente.vinculado_desde)}
                    </span>
                  </div>
                </div>

                {/* CTA */}
                <div className="flex-shrink-0">
                  <span className="flex items-center gap-1 text-sm font-medium text-teal-600 group-hover:text-teal-800 transition-colors">
                    Gestionar
                    <FiArrowRight className="h-4 w-4 group-hover:translate-x-0.5 transition-transform" />
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

export default MisClientes
